import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import { ToastContainer } from 'react-toastify';
import ToasterHelper from '../../../shared/helpers/toaster.helper';
import { ISignUp } from '../../../models/interfaces/signup.interface';
import AlertMessage from '../../alert-message/alert-message';
import Request from '../../../shared/request/Request';
import { API } from '../../../shared/constants/API';

import 'react-datepicker/dist/react-datepicker.css';
import { HttpStatusCode } from '../../../shared/request/HttpStatusCode';
import { FiInfo } from 'react-icons/fi';
import { Checkbox } from 'primereact/checkbox';
import { IUser } from '../../../models/interfaces/user.interface';
import { getItem, setItem } from '../../../services/session-storage.service';
import { isUserOlderThan15 } from '../../../shared/helpers/utils.helper';

function UserSettings() {
  const { t } = useTranslation();
  const user = JSON.parse(getItem('user')) as IUser;
  const [name, setName] = useState(user.name);
  const [isActive, setIsActive] = useState(user.isActive);
  const [validated, setValidated] = useState(false);
  const [lastNameInvalid, setLastNameInvalid] = useState(false);
  const [email, setEmail] = useState(user.email);
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [message, setMessage] = useState('');
  const [type] = useState('');
  const [country, setCountry] = useState(user.countryId);
  const [countries, setCountries] = useState([]);

  const [dateOfBirth, setDateOfBirth] = useState(user.dateOfBirth);
  const [userDateOfBirth, setUserDateOfBirth] = useState(new Date(user.dateOfBirth));

  var years = [];
  const startingYear = 1930;
  for (var i = startingYear; i <= 2100; i++) {
    years.push(i);
  }
  // TODO - Add translations
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    if (countries.length === 0) {
      Request.get(`${API.USERS_SERVICE}/getCountries`, {})
        .then((response: any) => {
          if (response.body?.countries) {
            setCountries(response.body.countries);
          } else {
            if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
              ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
            }
            return [];
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
          return [];
        });
    }
  }, [countries, t]);

  const handleUserDateOfBirthSelect = (date: any) => {
    let dt: any = '';

    if (date != null) {
      dt = `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}-${
        date.getDate() + 1 < 10 ? '0' : ''
      }${date.getDate()}`;
    }
    if (isUserOlderThan15(date)) {
      setDateOfBirth(dt);
    } else {
      ToasterHelper.notify(t('COMMON.USER_OLDER_THAN_15'), 'error');
    }
  };

  const onUpdateSettings = (event: any) => {
    setMessage('');

    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;

    if (email && form.checkValidity() === true) {
      const input: ISignUp = {
        id: user.id,
        name: form.name.value,
        email: form.email.value,
        password: form.password.value,
        dateOfBirth: dateOfBirth,
        countryId: country,
        isActive: isActive
      };

      Request.put(`${API.USERS_SERVICE}/updateUserSettings`, { body: input })
        .then((result: any) => {
          if (result.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
            ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
          } else if (result.status.toString() === HttpStatusCode.OK) {
            updateUser(input);
            ToasterHelper.notify(t('USERS.USER_UPDATED_SUCCESSFULLY'), 'success');
          } else {
            ToasterHelper.notify(t('COMPLETE_PROCESS.FAILED_MESSAGE'), 'error');
          }
        })
        .catch((error: any) => {
          ToasterHelper.notify(t('COMPLETE_PROCESS.FAILED_MESSAGE'), 'error');
        });
    }
    setValidated(true);
  };

  const updateUser = (input: ISignUp) => {
    const userTemp = {
      id: user.id,
      name: input.name,
      email: input.email,
      dateOfBirth: input.dateOfBirth,
      roleId: user.roleId,
      countryId: input.countryId,
      isActive: input.isActive,
      isBackOffice: user.isBackOffice
    } as IUser;
    setItem('user', JSON.stringify(userTemp));
  };

  const isValidEmail = (email: string) => {
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);
  };

  const validateEmail = (event: any) => {
    setEmail(event.target.value);
    if (!isValidEmail(event.target.value)) {
      setEmailInvalid(true);
    } else {
      setEmailInvalid(false);
    }
    event.preventDefault();
  };

  const validatePassword = (event: any) => {
    setPasswordInvalid(false);
    if (event.target.value.length > 0 && event.target.value.length < 2) {
      setPasswordInvalid(true);
    }
    event.preventDefault();
  };

  const validateUserName = (event: any) => {
    if (event.target.value.length < 2) {
      setLastNameInvalid(true);
    } else {
      setLastNameInvalid(false);
      setName(event.target.value);
    }
    event.preventDefault();
  };

  return (
    <>
      <div className='background h-full'>
        <section>
          <div className='pt-10'>
            <p className='text-center text-white'>
              <b>{t('USERS.SETTINGS.TITLE')}</b>
            </p>
          </div>
          {message !== '' && (
            <div style={{ margin: '50px 0px 0px 0px', width: '100%' }}>
              <AlertMessage translationKey={message} type={type} />
            </div>
          )}
          <ToastContainer />{' '}
          <Form noValidate validated={validated} onSubmit={onUpdateSettings}>
            <div className='flex flex-col items-center'>
              <div className='col-md-3 text-sm'>
                <div className='col-md-3 text-white'>{t('USERS.SETTINGS.USER_NAME') + ' *'}</div>
                <div className='col-md-9'>
                  <Form.Group controlId='name' className='min-w-80 form-column'>
                    <Form.Control
                      type='text'
                      required
                      value={name}
                      onChange={validateUserName}
                      placeholder={t('USERS.SETTINGS.USER_NAME') + ' *'}
                      maxLength={50}
                    />
                    <div>
                      {lastNameInvalid && <small className='text-danger'>{t('USERS.SETTINGS.VALIDATIONS.USER_NAME_REQUIRED')}</small>}
                    </div>
                  </Form.Group>
                </div>
              </div>
              <div className='col-md-3 text-sm mt-3'>
                <div className='col-md-3 text-white'>{t('COMMON.COUNTRY') + ' *'}</div>
                <div className='col-md-9'>
                  <select
                    value={country}
                    onChange={e => setCountry(parseInt(e.target.value, 10))}
                    className='form-control border-1 rounded-1 text-sm min-h-8 min-w-80 pl-2 bg-white'
                    required>
                    <option key='country.id_country.name' value=''></option>
                    {countries.map(country => (
                      <option key={country.id + country.name} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className='col-md-3 text-sm mt-3'>
                <div className='col-md-4 text-white'>{t('USERS.SETTINGS.USER_DATE_OF_BIRTH') + ' *'}</div>
                <div className='flex col-md-4'>
                  <div
                    className='col-md-4 border-1 rounded-1 min-w-52 bg-white'
                    style={{
                      zIndex: 9999
                    }}>
                    <DatePicker
                      calendarClassName='calendar'
                      placeholderText={`${t('USERS.SETTINGS.USER_DATE_OF_BIRTH') + ' *'}`}
                      required
                      showIcon
                      className='calendar'
                      disabledKeyboardNavigation
                      selected={userDateOfBirth}
                      onChange={setUserDateOfBirth}
                      onSelect={handleUserDateOfBirthSelect}
                      dateFormat={'yyyy-MM-dd'}
                      renderCustomHeader={({ date, changeYear, changeMonth }) => (
                        <div
                          style={{
                            margin: 10,
                            display: 'flex',
                            justifyContent: 'center',
                            zIndex: 9999
                          }}>
                          <select
                            value={date.getFullYear()}
                            onChange={({ target: { value } }) => changeYear(years[parseInt(value, 10) - startingYear])}
                            style={{
                              borderRadius: 5,
                              width: 60,
                              height: 30,
                              zIndex: 9999
                            }}>
                            {years.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                          <select
                            value={months[date.getMonth()]}
                            onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                            style={{
                              marginLeft: 30,
                              borderRadius: 5,
                              width: 100,
                              height: 30,
                              zIndex: 9999
                            }}>
                            {months.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    />
                  </div>
                  <div className='col-md-4 text-white text-base mt-2 pl-2'>
                    <span title='We will use your date of birth to calculate your age group.'>
                      <FiInfo />
                    </span>
                  </div>
                </div>
              </div>
              <div className='col-md-3 text-sm mt-3'>
                <div className='col-md-3 text-white'>{t('USERS.SETTINGS.USER_EMAIL') + ' *'}</div>
                <div className='col-md-9'>
                  <Form.Group controlId='email' className='min-w-80 form-column'>
                    <Form.Control
                      type='email'
                      required
                      value={email}
                      onChange={validateEmail}
                      placeholder={t('USERS.SETTINGS.USER_EMAIL') + ' *'}
                      maxLength={100}
                    />
                    <div>{emailInvalid && <small className='text-danger'>{t('USERS.SETTINGS.VALIDATIONS.USER_EMAIL_VALID')}</small>}</div>
                  </Form.Group>
                </div>
              </div>
              <div className='col-md-3 text-sm mt-3'>
                <div className='col-md-3 text-white'>{t('COMPLETE_PROCESS.PASSWORD') + ' *'}</div>
                <div className='col-md-9'>
                  <Form.Group controlId='password' className='min-w-80 form-column'>
                    <Form.Control
                      type='password'
                      onChange={validatePassword}
                      placeholder={t('COMPLETE_PROCESS.PASSWORD') + ' *'}
                      maxLength={50}
                    />
                    <div>
                      {passwordInvalid && <small className='text-danger'>{t('COMPLETE_PROCESS.VALIDATIONS.PASSWORD_REQUIRED')}</small>}
                    </div>
                  </Form.Group>
                </div>
              </div>
              <div className='col-md-3 text-sm mt-3'>
                <div className='col-md-3 text-white'>{t('USERS.SETTINGS.IS_ACTIVE') + ' *'}</div>
                <div className='col-md-9'>
                  <Checkbox className='' onChange={e => setIsActive(e.checked)} checked={isActive}></Checkbox>
                </div>
              </div>
            </div>
            <div className='text-center mt-5 pb-5'>
              <Button
                role='submit-button'
                className='button-font-size background-complete-process button-border-radius cursor-pointer'
                variant='primary'
                type='submit'>
                <b>{t('COMMON.SUBMIT')}</b>
              </Button>
            </div>
          </Form>
        </section>
      </div>
    </>
  );
}

export default UserSettings;
